import React, { useEffect, useRef } from 'react';

interface MagneticButtonProps {
  children: React.ReactNode;
  className?: string;
  magneticStrength?: number;
  onClick?: () => void;
  href?: string;
  as?: 'button' | 'a' | 'div';
}

const MagneticButton: React.FC<MagneticButtonProps> = ({
  children,
  className = '',
  magneticStrength = 20,
  onClick,
  href,
  as = 'button'
}) => {
  const buttonRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      
      const distance = Math.sqrt(x * x + y * y);
      const maxDistance = Math.max(rect.width, rect.height);
      
      if (distance < maxDistance) {
        const strength = (1 - distance / maxDistance) * magneticStrength;
        const moveX = (x / maxDistance) * strength;
        const moveY = (y / maxDistance) * strength;
        
        button.style.transform = `translate3d(${moveX}px, ${moveY}px, 0) scale(1.05)`;
      }
    };

    const handleMouseLeave = () => {
      button.style.transform = 'translate3d(0, 0, 0) scale(1)';
    };

    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [magneticStrength]);

  const commonProps = {
    ref: buttonRef as any,
    className: `${className} transition-transform duration-300 ease-out`,
    style: { willChange: 'transform' }
  };

  if (as === 'a' && href) {
    return (
      <a {...commonProps} href={href} onClick={onClick}>
        {children}
      </a>
    );
  }

  if (as === 'div') {
    return (
      <div {...commonProps} onClick={onClick}>
        {children}
      </div>
    );
  }

  return (
    <button {...commonProps} onClick={onClick}>
      {children}
    </button>
  );
};

export default MagneticButton;